import styled, { css } from 'styled-components';
import Icon from 'src/components/Icon';
import { Color } from 'src/utils/theme';

export const Action = styled.div``;

export const Container = styled.div<{
  $borderColor: Color;
}>(
  ({ theme, $borderColor }) => css`
    position: relative;
    width: ${theme.sz.ml};
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    background-color: ${theme.co.secondary20};
    color: ${theme.co.neutral99};
    padding: ${theme.sz.s3} ${theme.sz.s5};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s3} ${theme.sz.s4};
    }

    @media (max-width: ${theme.sz.s}) {
      padding: ${theme.sz.s3} ${theme.sz.s0};
    }

    &:before {
      display: block;
      content: '';
      position: absolute;
      top: -0.45rem;
      left: 0;
      right: 0;
      border-top: ${theme.sz.s2} solid ${theme.co[$borderColor]};
      border-top-left-radius: ${theme.sz.s3};
      border-top-right-radius: ${theme.sz.s3};
    }
  `,
);

export const SlidesContainer = styled.div<{
  $withSlider?: boolean;
  $isSlideMoving?: boolean;
}>(
  ({ theme, $withSlider, $isSlideMoving }) => css`
    position: relative;
    width: ${theme.sz.ml};
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    background-color: ${theme.co.secondary20};
    color: ${theme.co.neutral99};
    padding: ${theme.sz.s1} ${theme.sz.s6};

    ${$withSlider
      ? css`
          padding-bottom: ${theme.sz.s8};
        `
      : css`
          padding-left: ${theme.sz.s4};
          padding-right: ${theme.sz.s4};
        `};

    @media (max-width: ${theme.sz.lg}) {
      padding-left: ${theme.sz.s0};
      padding-right: ${theme.sz.s0};
    }

    .slick-slide {
      /*! @noflip */
      float: left;

      &:not(.slick-active) {
        ${Action} {
          visibility: ${$isSlideMoving ? 'visible' : 'hidden'};
        }
      }
    }

    .slick-dots {
      position: absolute;
      max-width: unset;
      list-style: none;
      text-align: center;
      display: block;

      width: 100%;
      padding: 0;
      margin: 0;

      > li {
        position: relative;
        display: inline-block;
        width: ${theme.sz.s5};
        height: ${theme.sz.s5};
        cursor: pointer;

        > button {
          font-size: 0;
          line-height: 0;
          display: block;
          width: ${theme.sz.s5};
          height: ${theme.sz.s5};

          border: 0;
          background: transparent;
          color: ${theme.co.secondary70};

          &:before {
            content: '•';
            font-size: ${theme.sz.s8};
            color: ${theme.co.secondary60};

            position: absolute;
            top: 0;
            left: 0;
            line-height: ${theme.sz.s5};
            width: ${theme.sz.s5};
            height: ${theme.sz.s5};
          }
        }

        &.slick-active > button:before {
          color: ${theme.co.primary20};
        }
      }
    }
  `,
);

export const PrevArrowContainer = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: -${theme.sz.s6};

    ${
      /* istanbul ignore next: tested visually */ theme.rtl &&
      css`
        left: unset;
        right: -${theme.sz.s6};
      `
    }

    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const NextArrowContainer = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: -${theme.sz.s6};

    ${
      /* istanbul ignore next: tested visually */
      theme.rtl &&
      css`
        right: unset;
        left: -${theme.sz.s6};
      `
    }

    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const BackToHomeSection = styled.div(
  ({ theme }) => css`
    border-top: ${theme.bo.thinWidth} solid ${theme.co.secondary40};
    margin-top: ${theme.sz.s3};
    padding-top: ${theme.sz.s2};
    padding-bottom: ${theme.sz.s4};
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (max-width: ${theme.sz.s}) {
      margin-left: ${theme.sz.s2};
      margin-right: ${theme.sz.s2};
    }
  `,
);

export const LoadingContainer = styled.div(
  ({ theme }) => css`
    > div {
      min-height: ${theme.sz.s24};
    }
  `,
);

export const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s1} ${theme.sz.s2};
  `,
);

export const MainContent = styled.div(
  () => css`
    flex-grow: 1;
    overflow: hidden;
  `,
);

export const Title = styled.div(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

const ANIMATION_SPEED = 50;
const ANIMATION_MIN_DURATION_SEC = 3;
const ANIMATION_PAUSE_SEC = 2;
const ANIMATION_RIGHT_PADDING_PX = 4;

/* istanbul ignore next: tested visually */
export const Description = styled.div<{ $textLengthPx?: number; $visibleTextLengthPx?: number }>(
  ({ theme, $textLengthPx, $visibleTextLengthPx }) => {
    const hiddenLength =
      $textLengthPx && $visibleTextLengthPx && $textLengthPx > $visibleTextLengthPx
        ? $textLengthPx - $visibleTextLengthPx + ANIMATION_RIGHT_PADDING_PX
        : 0;
    const animationDuration = hiddenLength
      ? Math.round(Math.max(hiddenLength / ANIMATION_SPEED, ANIMATION_MIN_DURATION_SEC)) +
        2 * ANIMATION_PAUSE_SEC
      : 0;
    const animationPausePercent = Math.round((ANIMATION_PAUSE_SEC * 100) / animationDuration);

    return css`
      @keyframes movingText {
        0% {
          left: 0;
        }
        ${animationPausePercent + '%'} {
          left: 0;
        }
        ${100 - animationPausePercent + '%'} {
          left: -${hiddenLength}px;
        }
        100% {
          left: -${hiddenLength}px;
        }
      }

      color: ${theme.co.secondary70};
      ${theme.typography.paragraph.m};

      position: relative;
      white-space: nowrap;
      animation: movingText ${animationDuration}s linear infinite;
    `;
  },
);

export const ContentIcon = styled(Icon)<{
  $color: Color;
}>(
  ({ theme, $color }) => css`
    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }
    color: ${theme.co[$color]};
    margin-top: ${theme.sz.s1};
  `,
);
